import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@antimatterhq/ui/lib/utils';
import { Icon } from '../icon/icon';

const checkboxVariants = cva(
  [
    'ring-offset-background peer h-4 w-4 shrink-0 border',
    'bg-white dark:bg-transparent border-grey-300 dark:border-grey-650',
    'focus-visible:ring-blue-600 dark:focus-visible:ring-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'hover:bg-blue-600/8 dark:hover:bg-blue-400/8 box-content',
  ],
  {
    variants: {
      variant: {
        default: [
          'data-[state=checked]:bg-blue-600 dark:data-[state=checked]:bg-blue-500',
          'data-[state=checked]:text-white dark:data-[state=checked]:text-grey-800',
          'data-[state=indeterminate]:bg-blue-600 dark:data-[state=indeterminate]:bg-blue-500',
          'data-[state=indeterminate]:text-white dark:data-[state=indeterminate]:text-grey-800',
          'hover:border-blue-600 dark:hover:border-blue-500',
        ],
        success: [
          'data-[state=checked]:bg-green-600 dark:data-[state=checked]:bg-green-500',
          'data-[state=checked]:text-white',
          'hover:border-green-600 dark:hover:border-green-500',
        ],
        warning: [
          'data-[state=checked]:bg-yellow-600 dark:data-[state=checked]:bg-yellow-500',
          'data-[state=checked]:text-white',
          'hover:border-yellow-600 dark:hover:border-yellow-500',
        ],
        error: [
          'data-[state=checked]:bg-red-600 dark:data-[state=checked]:bg-red-500',
          'data-[state=checked]:text-white',
          'hover:border-red-600 dark:hover:border-red-500',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {
  className?: string;
  label?: string;
  hint?: string;
  checked?: boolean | 'indeterminate';
}

interface CheckboxGroupProps {
  children: React.ReactNode;
  orientation?: 'vertical' | 'horizontal';
  className?: string;
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, label, hint, variant = 'default', checked, ...props }, ref) => (
  <div className="flex flex-col gap-1.5">
    <div className="flex items-center gap-2">
      <CheckboxPrimitive.Root
        ref={ref}
        className={cn(
          checkboxVariants({ variant }),
          className,
          'rounded-[4px]',
        )}
        checked={checked}
        {...props}
      >
        <CheckboxPrimitive.Indicator
          className={cn('flex items-center justify-center text-current')}
        >
          {checked === 'indeterminate' ? (
            <Icon icon="lucide:minus" className="h-4 w-4" />
          ) : (
            <Icon icon="radix-icons:check" className="h-4 w-4" />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {label && (
        <label className="text-sm font-medium leading-none text-grey-800 peer-disabled:cursor-not-allowed peer-disabled:opacity-70 dark:text-grey-100">
          {label}
        </label>
      )}
    </div>
    {hint && (
      <small className="text-xs text-grey-500 dark:text-grey-350">{hint}</small>
    )}
  </div>
));

const CheckboxGroup = ({
  children,
  orientation = 'vertical',
  className,
}: CheckboxGroupProps) => (
  <div
    className={cn(
      'flex',
      orientation === 'vertical' ? 'flex-col gap-4' : 'flex-row gap-8',
      className,
    )}
  >
    {children}
  </div>
);

Checkbox.displayName = 'Checkbox';
CheckboxGroup.displayName = 'CheckboxGroup';

export { Checkbox, CheckboxGroup };
export type { CheckboxProps, CheckboxGroupProps };
